import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { GuestClaimInfo, GuestClaimResult, SupportedLanguage } from "../../shared/models/player";
import { PlayerService } from "../../shared/services/player.service";
import { AnalyticsService } from "../../shared/services/analytics.service";
import { BehaviorSubject } from "rxjs";
import { timeout } from "rxjs/operators";
import { AuthRedirectService } from "../../shared/services/auth-redirect.service";
import { LocaleService } from "../../shared/i18n/locale.service";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: "app-guest-claim",
    templateUrl: "./guest-claim.component.html",
    styleUrls: ["../player.component.css"],
    standalone: false
})
export class GuestClaimComponent implements OnDestroy, OnInit {
  private static readonly EMAIL_NOT_VERIFIED = "EMAIL_NOT_VERIFIED";

  public token: string;
  public claimInfo: GuestClaimInfo;
  public loading = true;
  public loadingVisible = false;
  public readonly claimReady$ = new BehaviorSubject<boolean>(false);
  public claimInProgress = false;
  public emailVerificationInProgress = false;
  public emailNotVerified = false;
  public emailVerificationSent = false;
  public message: string;
  public errorMsg: string;
  public diagnosticMsg: string;
  public isAuthenticated = false;
  public showLanguageSelection = false;
  public selectedLanguage: SupportedLanguage = "en";
  private loadingTimer: number;

  constructor(
    public auth: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private playerService: PlayerService,
    private analytics: AnalyticsService,
    private authRedirect: AuthRedirectService,
    private localeService: LocaleService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.params["token"];
    this.auth.isAuthenticated$.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
    });

    const cachedLanguage = this.localeService.cachedLanguage();
    if (!cachedLanguage) {
      this.selectedLanguage = this.defaultPublicLanguage();
      this.showLanguageSelection = true;
      this.hideLoading();
      this.claimReady$.next(false);
      return;
    }

    this.selectedLanguage = cachedLanguage;
    this.localeService.use(cachedLanguage).subscribe(
      () => this.loadClaimInfo(),
      () => this.loadClaimInfo()
    );
  }

  public selectLanguage(language: SupportedLanguage): void {
    this.selectedLanguage = language;
    this.showLanguageSelection = false;
    this.showLoading();
    this.localeService.usePublic(language).subscribe(
      () => this.loadClaimInfo(),
      () => this.loadClaimInfo()
    );
  }

  public ngOnDestroy(): void {
    this.hideLoading();
  }

  public isSelectedLanguage(language: SupportedLanguage): boolean {
    return this.selectedLanguage === language;
  }

  private defaultPublicLanguage(): SupportedLanguage {
    const browserLanguages = typeof navigator !== "undefined" && navigator.languages
      ? navigator.languages
      : [];

    return browserLanguages.some((language) => language && language.toLowerCase().indexOf("hr") === 0)
      ? "hr"
      : "en";
  }

  public loadClaimInfo(): void {
    this.showLoading();
    this.errorMsg = null;
    this.diagnosticMsg = null;
    this.claimReady$.next(false);
    console.info("[guest-claim] Loading public claim preview.");
    this.playerService.getGuestClaimInfo(this.token).pipe(
      timeout(15000)
    ).subscribe(
      (claimInfo) => {
        console.info("[guest-claim] Public claim preview loaded.", { valid: claimInfo && claimInfo.valid });
        this.claimInfo = claimInfo;
        this.errorMsg = claimInfo && claimInfo.valid === false ? this.translate.instant("guestClaim.errors.invalidLink") : null;
        this.hideLoading();
        this.claimReady$.next(true);
      },
      (error) => {
        console.error("[guest-claim] Public claim preview failed.", {
          status: error && error.status,
          name: error && error.name,
          message: error && error.message
        });
        this.errorMsg = this.translate.instant("guestClaim.errors.invalidLink");
        this.diagnosticMsg = this.getClaimPreviewDiagnostic(error);
        this.hideLoading();
        this.claimReady$.next(true);
      }
    );
  }

  private showLoading(): void {
    this.loading = true;
    this.loadingVisible = false;
    this.clearLoadingTimer();
    this.loadingTimer = window.setTimeout(() => {
      this.loadingVisible = this.loading;
      this.loadingTimer = null;
    }, 300);
  }

  private hideLoading(): void {
    this.loading = false;
    this.loadingVisible = false;
    this.clearLoadingTimer();
  }

  private clearLoadingTimer(): void {
    if (!this.loadingTimer) {
      return;
    }
    window.clearTimeout(this.loadingTimer);
    this.loadingTimer = null;
  }

  public login(): void {
    this.analytics.track("guest_claim_started", { screen: "guest_claim" });
    this.authRedirect.login(this.router.url).subscribe();
  }

  public claimProfile(): void {
    this.analytics.track("guest_claim_started", { screen: "guest_claim" });
    this.claimInProgress = true;
    this.emailNotVerified = false;
    this.emailVerificationSent = false;
    this.playerService.claimGuestProfile(this.token).subscribe(
      (result) => {
        this.message = this.getSuccessMessage(result);
        this.errorMsg = null;
        this.refreshCurrentPlayer();
      },
      (res) => {
        if (this.isEmailNotVerifiedError(res)) {
          this.emailNotVerified = true;
          this.errorMsg = null;
          this.message = null;
          this.claimInProgress = false;
          return;
        }

        this.errorMsg = this.getClaimErrorMessage(res);
        this.claimInProgress = false;
      }
    );
  }

  public sendVerificationEmail(): void {
    this.emailVerificationInProgress = true;
    this.errorMsg = null;
    this.message = null;
    this.playerService.sendEmailVerification().subscribe(
      () => {
        this.emailVerificationSent = true;
        this.emailVerificationInProgress = false;
      },
      (res) => {
        if (this.isAlreadyVerifiedResponse(res)) {
          this.emailNotVerified = false;
          this.emailVerificationSent = false;
          this.emailVerificationInProgress = false;
          this.claimProfile();
          return;
        }

        this.errorMsg = this.translate.instant("notifications.emailVerificationSendFailed");
        this.emailVerificationInProgress = false;
      }
    );
  }

  private refreshCurrentPlayer(): void {
    this.playerService.getCurrentUserProfile().subscribe(
      (player) => {
        this.playerService.setCurrentPlayer(player);
        localStorage.removeItem("event");
        localStorage.removeItem("eventForPlayerDetail");
        localStorage.removeItem("peiForPlayerDetail");
        localStorage.removeItem("chatPlayer");
        this.claimInProgress = false;
      },
      () => {
        this.claimInProgress = false;
      }
    );
  }

  private getSuccessMessage(result: GuestClaimResult): string {
    if (!result) {
      return this.translate.instant("guestClaim.success.claimed");
    }

    if (result.result === "GUEST_MERGED") {
      return this.translate.instant("guestClaim.success.merged");
    }

    if (result.result === "ALREADY_CLAIMED") {
      return this.translate.instant("guestClaim.success.alreadyClaimed");
    }

    return this.translate.instant("guestClaim.success.claimed");
  }

  private getClaimErrorMessage(res: any): string {
    const backendMessage = res && res.error && (res.error.errorMsg || res.error.message || res.error.error)
      ? (res.error.errorMsg || res.error.message || res.error.error).toString().toLowerCase()
      : "";

    if (res && res.status === 400 && backendMessage.indexOf("email") > -1) {
      return this.translate.instant("guestClaim.errors.emailMismatch");
    }

    if (backendMessage.indexOf("expired") > -1 || backendMessage.indexOf("revoked") > -1 || backendMessage.indexOf("invalid") > -1) {
      return this.translate.instant("guestClaim.errors.askManagerNewInvite");
    }

    return this.translate.instant("guestClaim.errors.askManagerNewInvite");
  }

  private getClaimPreviewDiagnostic(error: any): string {
    if (error && error.name === "TimeoutError") {
      return this.translate.instant("guestClaim.diagnostics.timeout");
    }

    if (error && error.status) {
      return this.translate.instant("guestClaim.diagnostics.httpStatus", { status: error.status });
    }

    if (error && error.message) {
      return this.translate.instant("guestClaim.diagnostics.message", { message: error.message });
    }

    return this.translate.instant("guestClaim.diagnostics.noResponse");
  }

  private isEmailNotVerifiedError(res: any): boolean {
    return res && res.status === 400 && this.getBackendMessage(res).indexOf(GuestClaimComponent.EMAIL_NOT_VERIFIED) > -1;
  }

  private isAlreadyVerifiedResponse(res: any): boolean {
    const backendMessage = this.getBackendMessage(res);
    return backendMessage.indexOf("ALREADY_VERIFIED") > -1 || backendMessage.indexOf("EMAIL_ALREADY_VERIFIED") > -1 || backendMessage.indexOf("already verified") > -1;
  }

  private getBackendMessage(res: any): string {
    if (!res || !res.error) {
      return "";
    }

    return (res.error.errorMsg || res.error.message || res.error.error || res.error || "").toString();
  }
}
